import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {OptableLayout} from '../../../../core/components/layout/optable-layout.interface'
import {Observable, Subject} from 'rxjs'
import {TopicOverviewPageResolved} from './topic-overview.resolver'
import {debounceTime, map, take, takeUntil} from 'rxjs/operators'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {ActivatedRoute, Router} from '@angular/router'
import {TranslocoService} from '@ngneat/transloco'
import {Breadcrumb} from '../../../../craft/breadcrumbs/breadcrumbs.component'
import {FormControl} from '@angular/forms'
import {ArticlePreview} from '../../../../core/content/types/article.types'
import {LinkService} from '../../../../core/i18n/link.service'
import {pickProperty} from '../../../../core/graphql/graphql-utils'
import {faInfoCircle} from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import {
  TopicOverviewPage_SearchArticlesGql,
  TopicOverviewPage_SearchArticlesQuery,
  TopicOverviewPage_SearchArticlesQueryVariables,
} from '../../../../../__generated__/types'
import {QueryRef} from 'apollo-angular'
import {parseArticlePreview} from '../../../../core/content/serde/article-page.serde'
import {Language} from '../../../../core/i18n/language'

@Component({
  templateUrl: './topic-overview-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopicOverviewPageComponent extends PageComponent implements OptableLayout, OnDestroy, OnInit {
  readonly topic$: Observable<TopicOverviewPageResolved>
  readonly breadcrumbs$: Observable<Breadcrumb[]>
  articles$?: Observable<{total: number; items: ArticlePreview[]}>
  latestArticles$?: Observable<ArticlePreview[]>

  // OptableLayout
  readonly optOutOfLayout = true
  readonly transparentNavbar = false

  // search
  activatedSearchTerm?: string
  readonly searchControl = new FormControl('')
  private searchArticlesQuery?: QueryRef<
    TopicOverviewPage_SearchArticlesQuery,
    TopicOverviewPage_SearchArticlesQueryVariables
  >
  readonly pageSize = 18 // // load a multiple of 2 to fill the rows regardless of screen resolution (grid is 1 or 2 rows)
  readonly isLoadingMore$ = new Subject<boolean>()

  readonly infoIcon = faInfoCircle
  private readonly unsubscribe$ = new Subject<void>()

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly searchArticlesGql: TopicOverviewPage_SearchArticlesGql,
    private readonly linkService: LinkService,
    transloco: TranslocoService,
  ) {
    super()
    this.topic$ = this.route.data.pipe(map(data => data['content']))
    this.breadcrumbs$ = this.topic$.pipe(
      map(topic => {
        return [
          {
            name: transloco.translate('article.overview.title'),
            link: linkService.localizedPathTo(PUBLIC_ROUTES.ARTICLE_OVERVIEW),
          },
          {name: topic.breadcrumbTitle || topic.title, link: this.router.url},
        ]
      }),
    )
  }

  ngOnInit() {
    // when navigating to another topic page from here, the component is reused
    // we need to reset the filters and re-wire the query
    this.topic$.pipe(takeUntil(this.unsubscribe$)).subscribe(topic => {
      this.activatedSearchTerm = undefined
      this.searchControl.reset('', {emitEvent: false})

      // now that we have topic, wire the query
      this.searchArticlesQuery = this.searchArticlesGql.watch({
        page: {offset: 0, limit: this.pageSize},
        topic: topic.slug,
      })
      this.articles$ = this.searchArticlesQuery.valueChanges.pipe(
        map(res => pickProperty(res.data, 'searchArticles')),
        map(({items, total}) => ({
          total,
          items: items.map(a => parseArticlePreview(a, this.linkService)),
        })),
      )
      this.latestArticles$ = this.articles$.pipe(
        take(1), // we only want the first value of the topic articles, and should not be affected by the filters
        map(articles => articles.items.slice(0, 5)),
      )
    })
    // wire filters
    this.observeFilters()
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.topic$.pipe(
      map(topic => ({
        title: topic.title,
        description: topic.description,
      })),
    )
  }

  loadMoreArticles(offset: number) {
    if (!this.searchArticlesQuery) {
      return
    }
    this.isLoadingMore$.next(true)
    // in order to have pagination handled by apollo (using fetchMore), a type policy is defined in apollo-factory.ts,
    // to let apollo know how to merge the new data with the existing cache
    const page = {offset, limit: this.pageSize}
    this.searchArticlesQuery.fetchMore({variables: {page}}).then(() => {
      this.isLoadingMore$.next(false)
    })
  }

  private observeFilters() {
    this.searchControl.valueChanges.pipe(debounceTime(250), takeUntil(this.unsubscribe$)).subscribe(search => {
      if (!this.searchArticlesQuery) {
        return
      }
      const page = {offset: 0, limit: this.pageSize} // filters changed, reset pagination
      this.searchArticlesQuery
        .refetch({
          searchText: search || undefined,
          // refetch will keep the variables (topic) that are not changed
          page,
        })
        .then(() => {
          this.activatedSearchTerm = search || undefined
        })
    })
  }

  getPageAvailableLanguages(): Observable<Language[]> {
    return this.topic$.pipe(map(t => t.availableLanguages))
  }
}
